import { format, isValid, parse } from "date-fns";
import React from "react";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import Popover, { type PopoverProps } from "../../common/Popover/Popover";
import Calendar from "./Calendar";
import TimeInput from "./TimeInput";
import styles from "./DateTimeInput.module.scss";

const TIME_INPUT_FORMAT = "HH:mm";

const applyTimeToDate = (date: Date, time: string) => {
  const parsed = parse(time, TIME_INPUT_FORMAT, new Date());
  const nextDate = new Date(date);
  if (!isValid(parsed)) return nextDate;
  nextDate.setHours(parsed.getHours(), parsed.getMinutes(), 0, 0);
  return nextDate;
};

type DateTimePickerContextValue = {
  value?: Date | null;
  onValueChange?: (value: Date | null) => void;
  dateOnly: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  disabled?: boolean;
  visibleMonth?: Date;
  onVisibleMonthChange?: (value: Date | undefined) => void;
};

const DateTimePickerContext = React.createContext<DateTimePickerContextValue | null>(null);

export interface DateTimePickerProps extends PopoverProps {
  value?: Date | null;
  onValueChange?: (value: Date | null) => void;
  dateOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  disabled?: boolean;
  visibleMonth?: Date;
  onVisibleMonthChange?: (value: Date | undefined) => void;
}

const DateTimePickerRoot = ({
  value,
  onValueChange,
  dateOnly = false,
  minDate,
  maxDate,
  disabledDates,
  disabled,
  visibleMonth: controlledVisibleMonth,
  onVisibleMonthChange,
  children,
  mode = "click",
  placement = "bottom-start",
  interactable = true,
  ...popoverProps
}: DateTimePickerProps) => {
  const [uncontrolledVisibleMonth, setUncontrolledVisibleMonth] = React.useState<Date | undefined>(value ?? undefined);
  const visibleMonth = controlledVisibleMonth ?? uncontrolledVisibleMonth;
  const setVisibleMonth = React.useCallback(
    (nextVisibleMonth: Date | undefined) => {
      if (controlledVisibleMonth === undefined) {
        setUncontrolledVisibleMonth(nextVisibleMonth);
      }
      onVisibleMonthChange?.(nextVisibleMonth);
    },
    [controlledVisibleMonth, onVisibleMonthChange],
  );

  React.useEffect(() => {
    if (value) setVisibleMonth(value);
  }, [setVisibleMonth, value]);

  const contextValue = React.useMemo<DateTimePickerContextValue>(
    () => ({
      value,
      onValueChange,
      dateOnly,
      minDate,
      maxDate,
      disabledDates,
      disabled,
      visibleMonth,
      onVisibleMonthChange: setVisibleMonth,
    }),
    [dateOnly, disabled, disabledDates, maxDate, minDate, onValueChange, setVisibleMonth, value, visibleMonth],
  );

  return (
    <DateTimePickerContext.Provider value={contextValue}>
      <Popover
        {...popoverProps}
        mode={mode}
        placement={placement}
        interactable={interactable}
        disabled={disabled}
      >
        {children}
      </Popover>
    </DateTimePickerContext.Provider>
  );
};

export interface DateTimePickerContentProps
  extends Omit<React.ComponentProps<typeof FloatingPanel>, "children" | "value" | "onChange"> {
  value?: Date | null;
  onValueChange?: (value: Date | null) => void;
  dateOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  disabled?: boolean;
  visibleMonth?: Date;
  onVisibleMonthChange?: (value: Date | undefined) => void;
}

const DateTimePickerContent = ({
  className,
  value: propsValue,
  onValueChange: propsOnValueChange,
  dateOnly: propsDateOnly,
  minDate: propsMinDate,
  maxDate: propsMaxDate,
  disabledDates: propsDisabledDates,
  disabled: propsDisabled,
  visibleMonth: propsVisibleMonth,
  onVisibleMonthChange: propsOnVisibleMonthChange,
  ...props
}: DateTimePickerContentProps) => {
  const context = React.useContext(DateTimePickerContext);

  if (!context && propsValue === undefined && !propsOnValueChange) {
    return null;
  }

  const value = propsValue ?? context?.value ?? null;
  const onValueChange = propsOnValueChange ?? context?.onValueChange;
  const dateOnly = propsDateOnly ?? context?.dateOnly ?? false;
  const minDate = propsMinDate ?? context?.minDate;
  const maxDate = propsMaxDate ?? context?.maxDate;
  const disabledDates = propsDisabledDates ?? context?.disabledDates;
  const disabled = propsDisabled ?? context?.disabled;
  const visibleMonth = propsVisibleMonth ?? context?.visibleMonth;
  const onVisibleMonthChange = propsOnVisibleMonthChange ?? context?.onVisibleMonthChange;

  const timeValue = value ? format(value, TIME_INPUT_FORMAT) : "";
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onValueChange?.(null);
      return;
    }
    onValueChange?.(dateOnly ? date : applyTimeToDate(date, timeValue || "00:00"));
  };
  const handleTimeChange = (time: string) => {
    onValueChange?.(applyTimeToDate(value ?? new Date(), time));
  };

  return (
    <Popover.Content asChild>
      <FloatingPanel
        className={[styles.popoverContent, className].filter(Boolean).join(" ")}
        {...props}
      >
        <div onMouseDown={(event) => event.preventDefault()}>
          <Calendar
            mode="single"
            month={visibleMonth}
            onMonthChange={onVisibleMonthChange}
            selected={value ?? undefined}
            onSelect={handleDateSelect}
            disabled={
              disabled ||
              (disabledDates
                ? (date: Date) =>
                    disabledDates.some((disabledDate) => disabledDate.toDateString() === date.toDateString())
                : undefined)
            }
            fromDate={minDate}
            toDate={maxDate}
          />
        </div>
        {!dateOnly ? (
          <div className={styles.timePanel}>
            <TimeInput
              value={timeValue}
              onValueChange={handleTimeChange}
              disabled={disabled}
            />
          </div>
        ) : null}
      </FloatingPanel>
    </Popover.Content>
  );
};

const DateTimePicker = Object.assign(DateTimePickerRoot, {
  Reference: Popover.Reference,
  Trigger: Popover.Trigger,
  Content: DateTimePickerContent,
});

export default DateTimePicker;
